import type React from "react";
import { Check, CreditCard, MessageCircle, Pencil, Repeat, Trash2, User, Zap } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type RowSource = "whatsapp" | "cc-sync" | "recurring" | "web" | "automation";
export type RowMode = "grouped" | "list";

interface TransactionRowProps {
  id: string;
  description: string;
  amount: number;
  categoryName: string;
  categoryColor: string;
  paymentLabel: string;
  dateLabel?: string;
  source: RowSource;
  sourceLabel: string;
  installment?: { current: number; total: number };
  currencySymbol: string;
  locale: "en-US" | "he-IL";
  mode: RowMode;
  selected: boolean;
  showSourcePill: boolean;
  canEdit: boolean;
  editTooltip: string;
  editDisabledTooltip: string;
  deleteTooltip: string;
  onToggleSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const sourceStyles: Record<RowSource, string> = {
  whatsapp: "bg-[rgba(37,211,102,0.14)] text-[#25D366]",
  "cc-sync": "bg-[rgba(87,155,252,0.14)] text-[#579BFC]",
  recurring: "bg-[rgba(97,97,255,0.12)] text-[#6161FF]",
  web: "border border-[var(--border-color)] bg-transparent text-[var(--text-secondary)]",
  automation: "bg-[rgba(255,204,0,0.14)] text-[#FFCC00]",
};

const sourceIcons: Record<RowSource, React.ComponentType<{ size?: number; className?: string }>> = {
  whatsapp: MessageCircle,
  "cc-sync": CreditCard,
  recurring: Repeat,
  web: User,
  automation: Zap,
};

function withAlpha(hexColor: string): string {
  if (/^#[0-9A-Fa-f]{6}$/.test(hexColor)) {
    return `${hexColor}25`;
  }
  return "rgba(255,255,255,0.12)";
}

export default function TransactionRow({
  id,
  description,
  amount,
  categoryName,
  categoryColor,
  paymentLabel,
  dateLabel,
  source,
  sourceLabel,
  installment,
  currencySymbol,
  locale,
  mode,
  selected,
  showSourcePill,
  canEdit,
  editTooltip,
  editDisabledTooltip,
  deleteTooltip,
  onToggleSelect,
  onEdit,
  onDelete,
}: TransactionRowProps) {
  const SourceIcon = sourceIcons[source];
  const formattedAmount = `${currencySymbol}${Math.abs(amount).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggleSelect(id);
        }
      }}
      onClick={() => onToggleSelect(id)}
      className={cn(
        "group grid cursor-pointer grid-cols-[28px_28px_1fr_auto_auto_auto] items-center gap-3 border-b border-[var(--border-subtle)] px-4 py-3 transition-colors [transition-duration:150ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] hover:bg-[var(--bg-hover)]",
        "max-[700px]:grid-cols-[20px_22px_1fr_auto] max-[700px]:gap-2 max-[700px]:px-3 max-[700px]:py-2.5",
        selected && "bg-[rgba(97,97,255,0.08)]",
        !showSourcePill && "max-[700px]:grid-cols-[20px_22px_1fr_auto]",
      )}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={selected}
        aria-label={`Select transaction ${description}`}
        className={cn(
          "m-0 grid h-[18px] w-[18px] min-h-[18px] min-w-[18px] max-h-[18px] max-w-[18px] shrink-0 appearance-none place-items-center rounded-[5px] border border-[1.5px] border-[var(--border-color)] bg-transparent p-0 text-transparent outline-none max-[700px]:h-[16px] max-[700px]:w-[16px] max-[700px]:min-h-[16px] max-[700px]:min-w-[16px] max-[700px]:max-h-[16px] max-[700px]:max-w-[16px]",
          selected && "border-[var(--fg-accent)] bg-[var(--fg-accent)] text-white shadow-[0_0_0_2px_rgba(97,97,255,0.15)]",
        )}
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelect(id);
        }}
      >
        <Check size={12} />
      </button>

      <div
        className="grid h-7 w-7 place-items-center rounded-[8px] max-[700px]:h-[22px] max-[700px]:w-[22px] max-[700px]:rounded-[6px]"
        style={{ backgroundColor: withAlpha(categoryColor) }}
        aria-hidden
      >
        <span className="h-2 w-2 rounded-full max-[700px]:h-1.5 max-[700px]:w-1.5" style={{ backgroundColor: categoryColor }} />
      </div>

      <div className="min-w-0">
        <div className="flex items-center text-[13px] font-normal text-[var(--text-primary)] max-[700px]:text-[12px]">
          <span className="truncate">{description}</span>
          {installment && (
            <span className="ms-2 rounded-[100px] bg-[rgba(255,204,0,0.14)] px-[8px] py-[2px] text-[10px] font-medium text-[#FFCC00] max-[700px]:text-[9px]">
              {installment.current}/{installment.total}
            </span>
          )}
        </div>
        <div className="mt-[2px] flex flex-wrap items-center gap-[6px] text-[11px] font-normal text-[var(--text-muted)] max-[700px]:text-[10px]">
          <span style={{ color: categoryColor }}>{categoryName}</span>
          {mode === "list" && dateLabel ? (
            <>
              <span>·</span>
              <span>{dateLabel}</span>
            </>
          ) : null}
          <span>·</span>
          <span>{paymentLabel}</span>
          <span className="hidden max-[700px]:inline">·</span>
          <span className="hidden max-[700px]:inline">{sourceLabel}</span>
        </div>
      </div>

      {showSourcePill ? (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-[100px] px-[10px] py-[3px] text-[11px] font-medium max-[700px]:hidden",
            sourceStyles[source],
          )}
        >
          <SourceIcon size={11} />
          {sourceLabel}
        </span>
      ) : null}

      <div
        className={cn(
          "text-[14px] font-semibold text-[var(--text-primary)]",
          "max-[700px]:text-[12px]",
          amount > 0 && "text-[var(--income)]",
        )}
        style={{
          fontFamily: "var(--font-mono)",
          fontVariantNumeric: "tabular-nums",
          fontFeatureSettings: '"tnum"',
          letterSpacing: "-0.01em",
        }}
        dir="ltr"
      >
        {amount >= 0 ? "+" : "−"}
        {formattedAmount}
      </div>

      <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 max-[700px]:hidden">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={canEdit ? editTooltip : editDisabledTooltip}
              disabled={!canEdit}
              className={cn(
                "grid h-[26px] w-[26px] place-items-center rounded-[6px] text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-deep)] hover:text-[var(--text-primary)]",
                !canEdit && "cursor-not-allowed opacity-40 hover:bg-transparent hover:text-[var(--text-muted)]",
              )}
              onClick={(e) => {
                e.stopPropagation();
                if (canEdit) onEdit(id);
              }}
            >
              <Pencil size={13} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {canEdit ? editTooltip : editDisabledTooltip}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={deleteTooltip}
              className="grid h-[26px] w-[26px] place-items-center rounded-[6px] text-[var(--text-muted)] transition-colors hover:bg-[rgba(251,39,93,0.12)] hover:text-[var(--expense)]"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(id);
              }}
            >
              <Trash2 size={13} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {deleteTooltip}
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
